import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import useCanonical from "@/hooks/useCanonical";
import useMetaDescription from "@/hooks/useMetaDescription";

export default function TermsOfService() {
  useCanonical("https://ezeefitofficesystems.in/terms");
  useMetaDescription("Terms and conditions for Ezeefit Office Systems — modular office furniture manufacturer. Order terms, delivery, warranty, payment, and return policies.");
  useEffect(() => {
    document.title = "Terms & Conditions | Ezeefit Office Systems";
  }, []);

  return (
    <div className="pt-20" data-testid="terms-page">
      <Helmet>
        <meta name="description" content="Terms and conditions for Ezeefit Office Systems — modular office furniture manufacturer. Order terms, delivery, warranty, payment, and return policies." />
        <link rel="canonical" href="https://ezeefitofficesystems.in/terms" />
      </Helmet>

      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-8">Terms & Conditions</h1>
          <p className="text-sm text-[#64748B] mb-8">Last updated: April 2026</p>

          <div className="space-y-8 text-[#334155] leading-relaxed">
            <div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-3">1. Company Information</h2>
              <p>These terms govern the use of services provided by <strong>Ezeefit Modular Furniture Private Limited</strong> (operating under the brand "Ezeefit Office Systems"), a modular office furniture manufacturer with corporate office at 6th Floor, A/611-612, Crystal Plaza Premises, New Link Road, Andheri West, Mumbai 400053, Maharashtra, India. GSTIN: 27AADCE6117G1ZT.</p>
              <p className="mt-2">GSTIN: 27AADCE6117G1ZT</p>
              <p>Contact: <a href="tel:+919820596811" className="phone-flash">9820596811</a> / <a href="tel:+919820042100" className="text-[#F97316] hover:underline">9820042100</a> | hello@ezeefitofficesystems.in / eos@ezeefitofficesystems.in</p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-3">2. Products & Services</h2>
              <p>Ezeefit manufactures and supplies modular office furniture including workstations, task chairs, conference tables, partitions, phone booths, meeting pods, filing systems, locker systems, and related office furniture products. We also provide design consultation, space planning, manufacturing, delivery, and professional installation services.</p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-3">3. Orders & Quotations</h2>
              <p>All quotations are valid for 30 days from the date of issue unless otherwise specified. Prices are subject to change based on material costs and specifications. Orders are confirmed only upon receipt of the agreed advance payment and signed purchase order. Custom-manufactured products are made to order and cannot be cancelled once production has commenced.</p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-3">4. Payment Terms</h2>
              <p>Standard payment terms: 50% advance with order confirmation, balance prior to dispatch or as agreed in the purchase order. All payments are to be made via bank transfer, cheque, or other approved methods. GST at applicable rates will be charged on all invoices.</p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-3">5. Delivery & Installation</h2>
              <p>Delivery timelines are estimated and may vary based on order complexity, material availability, and site readiness. Standard delivery within Mumbai Metropolitan Region is 3-6 weeks from order confirmation. Pan-India deliveries may require additional time. Professional installation is included for orders above the specified threshold. The client must ensure site readiness (electrical, flooring, access) prior to the scheduled installation date.</p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-3">6. Warranty</h2>
              <p>Ezeefit provides a manufacturer's warranty on all products against defects in material and workmanship. Warranty duration varies by product category and is specified in the purchase order. Warranty does not cover damage caused by misuse, unauthorized modifications, natural wear and tear, or force majeure events.</p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-3">7. Returns & Exchanges</h2>
              <p>Custom-manufactured products are non-returnable. Standard products may be returned within 7 days of delivery if unused, in original packaging, and with prior written approval. A restocking fee of up to 20% may apply. Damaged or defective products will be replaced at no additional cost upon verification.</p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-3">8. Limitation of Liability</h2>
              <p>Ezeefit's liability is limited to the value of the products supplied. We are not liable for any indirect, consequential, or incidental damages arising from the use of our products or services. Force majeure events including natural disasters, pandemics, or government regulations may affect delivery timelines without liability.</p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-3">9. Intellectual Property</h2>
              <p>All designs, 3D layouts, and shop drawings created by Ezeefit remain the intellectual property of Ezeefit Office Systems and may not be shared with or used by third parties without written consent.</p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-3">10. Governing Law</h2>
              <p>These terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Mumbai, Maharashtra.</p>
            </div>

            <div className="border-t border-[#E2E8F0] pt-6">
              <p className="text-sm text-[#64748B]">For questions about these terms, contact us at <a href="mailto:hello@ezeefitofficesystems.in" className="text-[#F97316] hover:underline">hello@ezeefitofficesystems.in</a> / <a href="mailto:eos@ezeefitofficesystems.in" className="text-[#F97316] hover:underline">eos@ezeefitofficesystems.in</a> or call <a href="tel:+919820596811" className="phone-flash">9820596811</a> / <a href="tel:+919820042100" className="text-[#F97316] hover:underline">9820042100</a>.</p>
              <Link to="/privacy-policy" className="text-sm text-[#F97316] hover:underline mt-2 inline-block">View Privacy Policy</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
